import { useEffect, useMemo, useState } from 'react';
import { Menu } from 'antd';
import { useLocation } from 'react-router-dom';
import LazySvg from 'components/LazySvg';
import useSidebarMenuItems from './useSidebarMenuItems';
import { findActiveLeafKey, findSectionKeyForPath } from './menu-config';
import { SidebarMenuProps } from './types';
import styles from './index.module.scss';

// Sidebar menu component
function SidebarMenu({ onClose }: SidebarMenuProps) {
  const { pathname } = useLocation();
  const items = useSidebarMenuItems();

  // Which leaf owns the current route, matched by longest path prefix so detail
  // pages (e.g. /credit-report/:bureauId) keep their parent leaf highlighted.
  const activeLeafKey = useMemo(() => findActiveLeafKey(pathname), [pathname]);
  const activeSectionKey = useMemo(
    () => findSectionKeyForPath(pathname),
    [pathname],
  );

  // Open state is derived from the URL rather than persisted, so the section
  // containing the current page is expanded on load and after a refresh.
  const [openKeys, setOpenKeys] = useState<string[]>(
    activeSectionKey ? [activeSectionKey] : [],
  );

  // Navigating to a stand-alone item (e.g. Dashboard) has no owning section, so
  // clear the open keys to collapse whichever section was previously expanded.
  useEffect(() => {
    setOpenKeys(activeSectionKey ? [activeSectionKey] : []);
    // pathname is a dep so re-clicking the same stand-alone item also collapses
    // a section the user expanded manually while staying on that page.
  }, [activeSectionKey, pathname]);

  return (
    <Menu
      mode='inline'
      inlineIndent={0}
      items={items}
      selectedKeys={activeLeafKey ? [activeLeafKey] : []}
      openKeys={openKeys}
      // Keeping only the last key expanded gives the single-open accordion:
      // opening a section collapses whichever one was already open.
      onOpenChange={(keys) => setOpenKeys(keys.slice(-1) as string[])}
      expandIcon={<LazySvg name='chevronDown' className={styles.expandIcon} />}
      onClick={onClose}
    />
  );
}

export default SidebarMenu;
